import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  ListGroup,
  ListGroupItem,
  Button,
  Form,
  Collapse,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { MultiSelect } from "react-multi-select-component";
import { Link } from "react-router-dom";
import moment from "moment";
import minus_icon from "../../assets/images/icon/cross-plain.svg";
import plus_icon from "../../assets/images/icon/plus.svg";

// Initialize time slots structure for each day
const initialTimeSlots = {
  Sunday: [],
  Monday: [],
  Tuesday: [],
  Wednesday: [],
  Thursday: [],
  Friday: [],
  Saturday: [],
};

const generateTimeSlots = () => {
  const timeSlots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const hourStr = hour.toString().padStart(2, "0");
      const minStr = min.toString().padStart(2, "0");
      timeSlots.push(`${hourStr}:${minStr}`);
    }
  }
  return timeSlots;
};

const TimeslotBookingAdmin = ({
  onValueChange,
  onErrorHandle,
  defaultTimeSlots,
}) => {
  // Initialize the state with defaultTimeSlots or fallback to initialTimeSlots
  const [timeSlots, setTimeSlots] = useState(() => {
    const initialState = { ...initialTimeSlots };
    Object.keys(initialTimeSlots).forEach((day, index) => {
      initialState[day] = defaultTimeSlots[index] || [];
    });
    return initialState;
  });

  const [selectedTargetDays, setSelectedTargetDays] = useState({});
  const [pendingSourceDay, setPendingSourceDay] = useState("");
  const [errors, setErrors] = useState({}); // State to track validation errors
  const [value, setValue] = useState("");
  const [daytimeSlots, setDaytimeSlots] = useState("");

  // Update timeSlots when defaultTimeSlots change
  useEffect(() => {
    if (defaultTimeSlots) {
      const updatedTimeSlots = { ...initialTimeSlots };
      Object.keys(initialTimeSlots).forEach((day, index) => {
        updatedTimeSlots[day] = defaultTimeSlots[index] || [];
      });
      setTimeSlots(updatedTimeSlots); // Update the state with new time slots
    }
  }, [defaultTimeSlots]);

  useEffect(() => {
    onErrorHandle(errors); // Notify the parent component about the state change
  }, [errors]);

  // Update parent component when timeSlots state changes
  useEffect(() => {
    setValue(JSON.stringify(timeSlots));
    onValueChange(JSON.stringify(timeSlots)); // Notify the parent component
  }, [timeSlots]);

  // Handle checkbox toggling for each day
  const handleDayChange = day => {
    const startTime = "09:00";
    setTimeSlots(prev => ({
      ...prev,
      [day]:
        prev[day].length > 0
          ? []
          : [
              {
                startTime,
                endTime: moment(startTime, "HH:mm")
                  .add(30, "minutes")
                  .format("HH:mm"),
              },
            ],
    }));
    setErrors(prev => ({ ...prev, [day]: [] })); // Reset errors for this day
  };

  // Time overlap checking function
  const checkOverlap = (day, newStartTime, newEndTime, indexToExclude = -1) => {
    const daySlots = timeSlots[day];
    for (let i = 0; i < daySlots.length; i++) {
      if (i === indexToExclude) continue;
      const { startTime, endTime } = daySlots[i];
      if (
        (newStartTime >= startTime && newStartTime < endTime) || // New start overlaps existing
        (newEndTime > startTime && newEndTime <= endTime) || // New end overlaps existing
        (newStartTime <= startTime && newEndTime >= endTime) // New slot surrounds existing
      ) {
        return true;
      }
    }
    return false;
  };

  // Handle change in timeslot for a particular day
  const handleTimeslotChange = (day, index, field, value) => {
    setTimeSlots(prev => {
      const updatedTimeSlots = prev[day].map((slot, i) =>
        i === index ? { ...slot, [field]: value } : slot
      );

      const currentSlot = updatedTimeSlots[index];
      const { startTime, endTime } = currentSlot;

      let newErrors = { ...errors };

      // Check for "End time cannot be less than start time"
      if (field === "endTime" && startTime && value < startTime) {
        if (!newErrors[day]) newErrors[day] = [];
        newErrors[day][index] = "End time cannot be less than start time";
      } else if (field === "startTime" && endTime && endTime < value) {
        if (!newErrors[day]) newErrors[day] = [];
        newErrors[day][index] = "Start time cannot be greater than end time";
      } else if (
        startTime &&
        endTime &&
        checkOverlap(day, startTime, endTime, index)
      ) {
        // If no start/end time error, check for overlap
        if (!newErrors[day]) newErrors[day] = [];
        newErrors[day][index] = "Time slot overlaps with existing slot";
      } else {
        // Clear error if no validation fails
        if (newErrors[day]) {
          newErrors[day][index] = ""; // Clear specific error
          // If all errors for the day are empty, clear the day from errors
          if (newErrors[day].every(error => error === "")) {
            delete newErrors[day];
          }
        }
      }

      setErrors(newErrors);

      return {
        ...prev,
        [day]: updatedTimeSlots,
      };
    });
  };

  const addTimeslot = day => {
    const lastIndexVal = timeSlots[day][timeSlots[day].length - 1];
    const startTime = lastIndexVal.endTime ? lastIndexVal.endTime : "09:00";
    setTimeSlots(prev => ({
      ...prev,
      [day]: [
        ...prev[day],
        {
          startTime,
          endTime: moment(startTime, "HH:mm")
            .add(30, "minutes")
            .format("HH:mm"),
        },
      ],
    }));
    setErrors(prev => ({ ...prev, [day]: [...(prev[day] || []), ""] })); // Add an empty error state for the new slot
  };

  const removeTimeslot = (day, index) => {
    setTimeSlots(prev => {
      const updatedTimeSlots = prev[day].filter((_, i) => i !== index);
      return {
        ...prev,
        [day]: updatedTimeSlots,
      };
    });

    // Remove error when timeslot is removed
    setErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors[day]) newErrors[day].splice(index, 1);
      return newErrors;
    });
  };

  const handleTargetDaysChange = (day, selectedOptions) => {
    setSelectedTargetDays(prev => ({
      ...prev,
      [day]: selectedOptions,
    }));
    setPendingSourceDay(day);
  };

  const applyTimeslots = () => {
    if (pendingSourceDay && selectedTargetDays[pendingSourceDay].length > 0) {
      setTimeSlots(prev => {
        const updatedTimeSlots = { ...prev };
        const sourceTimeslots = prev[pendingSourceDay];

        selectedTargetDays[pendingSourceDay].forEach(option => {
          const targetDay = option.value;
          if (targetDay !== pendingSourceDay) {
            updatedTimeSlots[targetDay] = sourceTimeslots;
          }
        });

        return updatedTimeSlots;
      });
    }
  };

  // Update the day options to disable the selected days
  const dayOptions = Object.keys(timeSlots).map(day => ({
    label: day,
    value: day,
    disabled: timeSlots[day].length > 0, // Disable option if day is selected
  }));

  const timeSlotOptions = generateTimeSlots();

  return (
    <>
      {/* <h3>Weekly Hours</h3> */}
      <ListGroup>
        {Object.keys(timeSlots).map(day => (
          <ListGroupItem key={day} className="mb-3 border rounded">
            <div className="d-flex justify-content-between align-items-center  card-slot-timecard">
              <div className="time-slot-card">
                <Form.Check
                  type="checkbox"
                  checked={timeSlots[day].length > 0}
                  onChange={() => handleDayChange(day)}
                  label={day}
                />
                <MultiSelect
                  options={dayOptions}
                  value={selectedTargetDays[day] || []}
                  onChange={selectedOptions =>
                    handleTargetDaysChange(day, selectedOptions)
                  }
                  labelledBy="Copy times to..."
                  hasSelectAll={false}
                  disableSearch={true}
                  overrideStrings={{ selectSomeItems: "Copy times to..." }}
                />

                <div className="time-slot-booking-card-ntn">
                  <Button
                    className="mt-2"
                    variant="primary"
                    onClick={applyTimeslots}
                  >
                    Apply
                  </Button>
                </div>
              </div>

              {timeSlots[day].length > 0 && (
                <div className="time-slot-booking-card-ntn pluss">
                  <Button variant="primary" onClick={() => addTimeslot(day)}>
                    <img src={plus_icon} alt="plus" />
                  </Button>
                </div>
              )}
            </div>

            <Collapse in={timeSlots[day].length > 0}>
              <div className="timeslot-bane">
                <div className="time-card-index">
                  {timeSlots[day].map((slot, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center gap-2 mb-2  timeslot-add-c"
                    >
                      <Form.Group
                        controlId={`${day}-start-${index}`}
                        className="time-slot-booking-card"
                      >
                        {/* <Form.Label>Start Time</Form.Label> */}
                        <Form.Control
                          as="select"
                          value={slot.startTime}
                          onChange={e =>
                            handleTimeslotChange(
                              day,
                              index,
                              "startTime",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select Start Time</option>
                          {timeSlotOptions.map(time => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                      <Form.Group
                        controlId={`${day}-end-${index}`}
                        className="time-slot-booking-card"
                      >
                        {/* <Form.Label>End Time</Form.Label> */}
                        <Form.Control
                          as="select"
                          value={slot.endTime}
                          onChange={e =>
                            handleTimeslotChange(
                              day,
                              index,
                              "endTime",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select End Time</option>
                          {timeSlotOptions.map(time => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>

                      <div className="time-slot-booking-card cross">
                        <Button
                          variant="danger"
                          className="ml-2"
                          onClick={() => removeTimeslot(day, index)}
                        >
                          <img src={minus_icon} alt="minus" />
                        </Button>
                      </div>
                      {errors[day] && errors[day][index] && (
                        <div className="text-danger">{errors[day][index]}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Collapse>
          </ListGroupItem>
        ))}
      </ListGroup>
    </>
  );
};

export default TimeslotBookingAdmin;
